import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { sameOrgPlanningReviewScope } from './orgPlanningReviewKey';
import { useOrgPlanningReviewScope } from './useOrgPlanningReviewScope';
import {
  atlasDraftCanvasRefreshKey, claimAtlasDraftSaveStatus, useAtlasDraftSaveRecoveryStore,
} from '../stores/atlasDraftSaveRecoveryStore';

type CanvasProjectionState = 'absent' | 'loading' | 'settled';
export type AtlasDraftSaveState = 'idle' | 'saving' | 'refreshing' | 'failed' | 'unknown';

function useCanvasProjectionState(folderId: string, diagramId: string, headChangeSetId: string | undefined): CanvasProjectionState {
  const queryClient = useQueryClient();
  const subscribe = useCallback((notify: () => void) => queryClient.getQueryCache().subscribe(notify), [queryClient]);
  const snapshot = useCallback((): CanvasProjectionState => {
    if (!headChangeSetId) return 'absent';
    const queries = queryClient.getQueryCache().findAll({
      queryKey: ['atlas', 'organization-plan-projection', folderId, diagramId],
      predicate: (query) => query.queryKey[5] === headChangeSetId,
    });
    if (!queries.length) return 'absent';
    return queries.some((query) => query.state.data !== undefined || query.state.status === 'error') ? 'settled' : 'loading';
  }, [diagramId, folderId, headChangeSetId, queryClient]);
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/** Canvas draft save progress for one chart: in-flight or unresolved saves, then the accepted head's canvas refresh. */
export function useAtlasDraftSaveStatus(folderId: string, diagramId: string) {
  const scope = useOrgPlanningReviewScope(folderId, diagramId);
  const all = useAtlasDraftSaveRecoveryStore((state) => state.attempts);
  const attempts = Object.values(all).filter((attempt) => sameOrgPlanningReviewScope(attempt.scope, scope));
  const refresh = useAtlasDraftSaveRecoveryStore((state) => state.canvasRefreshes[atlasDraftCanvasRefreshKey(folderId, diagramId)]);
  const refreshHead = refresh && sameOrgPlanningReviewScope(refresh.scope, scope) ? refresh.headChangeSetId : undefined;
  const canvasProjection = useCanvasProjectionState(folderId, diagramId, refreshHead);
  const refreshing = canvasProjection === 'loading' && !attempts.some((attempt) => attempt.phase === 'saving');
  const state: AtlasDraftSaveState = attempts.some((attempt) => attempt.phase === 'unknown') ? 'unknown'
    : attempts.some((attempt) => attempt.phase === 'failed') ? 'failed'
    : attempts.some((attempt) => attempt.phase === 'saving') ? 'saving'
    : refreshing ? 'refreshing' : 'idle';
  return { scope, attempts, refreshHead, canvasProjection, refreshing, state };
}

export function useClaimAtlasDraftSaveStatus(folderId: string | null | undefined, diagramId: string | null | undefined, enabled: boolean): void {
  useEffect(() => {
    if (!enabled || !folderId || !diagramId) return;
    return claimAtlasDraftSaveStatus(folderId, diagramId);
  }, [diagramId, enabled, folderId]);
}

export function useAtlasDraftSaveStatusClaimed(folderId: string, diagramId: string): boolean {
  return useAtlasDraftSaveRecoveryStore((state) => (state.statusOwners[atlasDraftCanvasRefreshKey(folderId, diagramId)] ?? 0) > 0);
}
